<template>
  <div
    class="filter-component"
    :class="{ 'filter-component--disabled': disabled }"
    :aria-disabled="disabled || undefined"
  >
    <!-- Show expanded filter component when in expanded mode -->
    <ExpandedFilterComponent
      v-if="isExpandedMode && !disabled"
      :selectedFilters="selectedFilters"
      :initialFilters="expandedInitialFilters || initialFilters"
      :locale="locale"
      :subjectOptions="subjectOptions"
      :gradeOptions="gradeOptions"
      :hardwareOptions="hardwareOptions"
      :durationOptions="durationOptions"
      :tagOptions="tagOptions"
      @filtersChanged="onExpandedFiltersChanged"
      @close="toggleFilterMode"
    />

    <!-- Show regular hover filters when in hover mode -->
    <template v-else>
      <!-- WCAG 4.1.2: use <button> so keyboard/AT users can activate the filter panel -->
      <button
        type="button"
        class="filter-header"
        :class="{ 'filter-header--disabled': disabled }"
        :disabled="disabled"
        :aria-expanded="isExpandedMode"
        aria-controls="filter-navigation-region"
        @click="!disabled && toggleFilterMode()"
      >
        <div class="header-content">
          <span class="header-title">All Filters</span>
          <!-- Decorative icon: aria-hidden keeps it out of AT (WCAG 1.1.1) -->
          <div class="filter-icon" aria-hidden="true">
            <FilterIcon color="white" />
          </div>
        </div>
      </button>

      <!-- WCAG 1.3.1: landmark region for filter controls -->
      <div
        id="filter-navigation-region"
        role="navigation"
        aria-label="Filter options"
        class="filter-navigation"
        :class="{ 'filter-navigation--disabled': disabled }"
      >
        <!-- Show disabled message when disabled -->
        <!-- WCAG 4.1.3: role="status" announces state change to AT -->
        <div
          v-if="disabled"
          class="disabled-message"
          role="status"
          aria-live="polite"
        >
          <p>Filters are not available</p>
        </div>

        <!-- Show normal filters when not disabled -->
        <template v-else>
          <div class="mobile-filter-popup">
            <FilterPopup
              :subject-options="subjectOptions"
              :grade-options="gradeOptions"
              :focus-options="focusOptions"
              :hardware-options="hardwareOptions"
              :tag-options="tagOptions"
              :subjects="subject"
              :grades="grade"
              :focus="focus"
              :hardware="hardware"
              :duration="duration"
              :tags="tags"
              @filtersChanged="emitFiltersFromPopup"
            />
          </div>

          <div class="desktop-filters">
            <FilterDropdown
              v-model="selectedFree"
              :options="freeOptions"
              label="Free"
              single-checkbox
              @change="onFreeDropdownChange"
            />

            <FilterDropdown
              v-model="selectedPopular"
              :options="popularOptions"
              label="Popular"
              single-checkbox
              @change="onPopularDropdownChange"
            />

            <FilterDropdown
              v-model="grade"
              :options="gradeOptions"
              label="Grade"
              @change="onGradeChange"
              @clear="onGradeClear"
            />

            <FilterDropdown
              v-model="subject"
              :options="subjectOptions"
              label="Subject"
              @change="onSubjectChange"
              @clear="onSubjectClear"
            />

            <FilterDropdown
              v-model="duration"
              :options="durationOptions"
              label="Duration"
              @change="onDurationChange"
              @clear="onDurationClear"
            />

            <FilterDropdown
              v-model="hardware"
              :options="hardwareOptions"
              label="Required Equipment"
              @change="onHardwareChange"
              @clear="onHardwareClear"
            />

            <TagsFilter
              v-model="tags"
              :options="tagOptions"
              label="Tags"
              @change="onTagsChange"
              @clear="onTagsClear"
            />
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup>
/* eslint-disable */
import FilterIcon from "@/components/icons/FilterIcon.vue";
import ExpandedFilterComponent from "@/components/maker-cart/ExpandedFilterComponent.vue";
import FilterDropdown from "@/components/maker-cart/FilterDropdown.vue";
import FilterPopup from "@/components/maker-cart/FilterPopup.vue";
import TagsFilter from "@/components/maker-cart/TagsFilter.vue";
import { useFilterLogic } from "@/composeables/useFilterLogic";
import { defineEmits, defineProps } from "vue";

const emit = defineEmits(["filtersChanged", "modeChanged"]);

// Props to receive initial filter values from parent
const props = defineProps({
  selectedFilters: {
    type: Array,
    default: () => [],
  },
  initialFilters: {
    type: Object,
    default: () => ({
      subjects: [],
      grades: [],
      focus: [],
      hardware: [],
      duration: [],
      tags: [],
      sort: "newest",
      locale: "US",
      popular: false,
      free: false,
    }),
  },
  locale: {
    type: String,
    default: "US",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

// Use the filter logic composable
const {
  subject,
  grade,
  focus,
  hardware,
  duration,
  tags,
  popular,
  freeResources,
  selectedPopular,
  selectedFree,
  isExpandedMode,
  expandedInitialFilters,
  subjectOptions,
  gradeOptions,
  focusOptions,
  durationOptions,
  hardwareOptions,
  tagOptions,
  popularOptions,
  freeOptions,
  emitFiltersFromPopup,
  onSubjectChange,
  onSubjectClear,
  onGradeChange,
  onGradeClear,
  onDurationChange,
  onDurationClear,
  onHardwareChange,
  onHardwareClear,
  onTagsChange,
  onTagsClear,
  onPopularDropdownChange,
  onFreeDropdownChange,
  toggleFilterMode,
  onExpandedFiltersChanged,
} = useFilterLogic(props, emit);
</script>

<style scoped>
.filter-component {
  position: relative;
  box-shadow:
    0px 6px 8px 4px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  /* Allow dropdown panels to overflow to the right */
  overflow: visible;
  width: 100%;
}

/* Header section styling */
.filter-header {
  background-color: var(--green-dark-1);
  padding: 8px;
  border-radius: 12px 12px 0 0;
  cursor: pointer;
  transition: background-color 0.2s ease;
  /* Reset native <button> styles (WCAG 4.1.2) */
  width: 100%;
  border: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  text-align: left;
}

/* Visible focus ring for keyboard users (WCAG 2.4.7) */
.filter-header:focus-visible {
  outline: 3px solid var(--green-light-2);
  outline-offset: 2px;
}

.filter-header:hover {
  background-color: var(--green-light-2);
}

.filter-header--disabled {
  background-color: var(--white-inactive);
  cursor: not-allowed;
}

.filter-header--disabled:hover {
  background-color: var(--white-inactive);
}

.filter-header--disabled .header-title {
  color: var(--black-body-text);
}

.filter-header--disabled .filter-icon svg {
  stroke: var(--black-body-text);
  fill: var(--black-body-text);
  stroke-width: 1.5px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  height: 48px;
  border-radius: 12px;
}

.header-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #ffffff;
  letter-spacing: 0.024px;
}

.filter-icon {
  color: #ffffff;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Navigation section styling */
.filter-navigation {
  background-color: #ffffff;
  padding: 8px;
  border-radius: 0 0 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Mobile filter popup - visible on small screens */
.mobile-filter-popup {
  display: none;
}

/* Desktop filters styling */
.desktop-filters {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Disabled state styling */
.disabled-message {
  display: flex;
  flex-direction: column;
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  letter-spacing: 0.08px;
  justify-content: center;
  width: 100%;
}

.disabled-message p {
  margin: 0;
  line-height: 24px;
}

/* Custom checkbox filters to match FilterDropdown styling */
.filter-checkbox-wrapper {
  width: 100%;
}

.checkbox-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  height: 48px;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.checkbox-trigger:hover {
  background-color: #f9fafb;
}

.checkbox-trigger.active {
  background-color: #f0f9ff;
}

.trigger-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.item-counter {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  background: var(--primary-pink);
  padding-top: 2px;
  color: white;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  flex-shrink: 0;
  animation: counter-appear 0.2s ease-out;
}

.filter-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #007a50;
  letter-spacing: 0.024px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.filter-checkbox {
  opacity: 0;
  position: absolute;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark-custom {
  height: 18px;
  width: 18px;
  background-color: #ffffff;
  border: 2px solid #00925e;
  border-radius: 4px;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.checkbox-container:hover .checkmark-custom {
  border-color: #026843;
}

.filter-checkbox:checked ~ .checkmark-custom {
  background-color: #00925e;
  border-color: #00925e;
}

.checkmark-custom:after {
  content: "";
  position: absolute;
  display: none;
}

.filter-checkbox:checked ~ .checkmark-custom:after {
  display: block;
}

.checkbox-container .checkmark-custom:after {
  left: 5px;
  top: 2px;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* Animation for counter */
@keyframes counter-appear {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Hidden filters - keeps functionality intact */
.hidden-filters {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
}

/* Mobile responsive styling */
@media (max-width: 768px) {
  .desktop-filters {
    display: none;
  }

  .mobile-filter-popup {
    display: block;
  }

  .filter-component {
    box-shadow: none;
    border-radius: 0;
  }

  .filter-header {
    border-radius: 0;
  }

  .filter-navigation {
    border-radius: 0;
  }
}

@media (max-width: 480px) {
  .header-content {
    padding: 8px 12px;
  }

  .filter-navigation {
    padding: 6px;
  }

  .header-title {
    font-size: var(--fs-small);
  }

  .filter-label {
    font-size: var(--fs-small);
  }
}

/* Legacy checkbox styles for hidden functionality */
.checkbox-filter {
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}

.checkbox-label-custom {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-blue);
  line-height: 1.5;
  position: relative;
}

.checkbox-text {
  user-select: none;
  font-weight: 500;
}

.filter-checkbox-custom {
  opacity: 0;
  position: absolute;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  height: 18px;
  width: 18px;
  background-color: #ffffff;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.checkbox-label-custom:hover .checkmark {
  border-color: var(--primary-blue);
}

.filter-checkbox-custom:checked ~ .checkmark {
  background-color: var(--primary-blue);
  border-color: var(--primary-blue);
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.filter-checkbox-custom:checked ~ .checkmark:after {
  display: block;
}

.checkbox-label-custom .checkmark:after {
  left: 5px;
  top: 2px;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: #6b7280;
  color: white;
  border-radius: 50%;
  font-size: 10px;
  font-style: normal;
  font-weight: bold;
  cursor: help;
  line-height: 1;
  flex-shrink: 0;
}

.info-icon:hover {
  background: #374151;
}

@media (max-width: 480px) {
  .filter-component {
    display: none;
  }
}
</style>
